import React from 'react';
import {normaliseImagePath} from "../utilities";

// ----- Usage ----- //
/*****************************************************************************************

<div data-component="embedded-gallery" data-props='{
  "images": {{ get_images_as_json(category.lowerMedia, 'gallery', '1280')|json_encode }},
  "autoplay": true,
  "showControls": true
  "playSpeed" : 5000
}' ></div>


 DEFAULTS:

 autoplay: true,
 playSpeed: 5000,
 showControls: false

******************************************************************************************/

export default class EmbeddedGallery extends React.Component {

    constructor (props){
        super(props);
        this.state = {
            autoplay: this.props.autoplay === undefined ? true : this.props.autoplay,
            playSpeed: parseInt(this.props.playSpeed) || 5000,
            showControls: this.props.showControls === undefined ? false : this.props.showControls,
            dragStart: null,
            dragEnd: null,
            threshold: 150,
            activateListeners: false,
            activateTransition: false,
            position: 0,
            slideWidth: null
        };

        // Bindings
        this.autoplayHandler = this.autoplayHandler.bind(this);
        this.resizeHandlers = this.resizeHandlers.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleMouseOut = this.handleMouseOut.bind(this);
        this.handleSlideSize = this.handleSlideSize.bind(this);
        this.setThreshold = this.setThreshold.bind(this);
        this.moveSlide = this.moveSlide.bind(this);
        this.renderSlide = this.renderSlide.bind(this);

        // Refs
        this.carousel = React.createRef();
        this.carouselTrack = React.createRef();
    }

    componentDidMount(){
        // Move last slide to the left - Infinite carousel
        if(this.props.images.length > 2){
            const firstSlide = this.carousel.current.firstElementChild;
            const lastSlide = this.carousel.current.lastElementChild;
            firstSlide.insertAdjacentElement('beforebegin', lastSlide);
        }

        // Set carousel width and threshold on load/resize
        this.handleSlideSize();
        this.setThreshold();
        window.addEventListener('resize', this.resizeHandlers);

        // Autoplay
        if(this.state.autoplay && this.props.images.length > 1){
            this.autoplayHandler();
        }
    }

    componentWillUnmount(){
        window.removeEventListener('resize', this.resizeHandlers);
    }

    autoplayHandler(){
        // If user scrolls slides, reset timer
        clearInterval(this.autoplay);
        this.autoplay = setInterval(() => {
            this.moveSlide(-1);
        }, this.state.playSpeed);
    }

    resizeHandlers(){
        this.handleSlideSize();
        this.setThreshold();
    }

    handleSlideSize(){
        this.setState({slideWidth: this.carouselTrack.current.offsetWidth});
    }

    handleMouseDown(e){
        if(this.props.images.length > 1) {
            // Check touches
            if(e.touches) e.pageX = e.touches[0].pageX;

            this.setState({dragStart: e.pageX, activateListeners: true});
        }
    }

    handleMouseMove(e){
        if(this.state.activateListeners){
            // Check touches
            if(e.touches) e.pageX = e.touches[0].pageX;

            this.setState({dragEnd: e.pageX}, () => {
                let dragPos = this.state.dragEnd - this.state.dragStart;
                this.setState({position: dragPos});
            });
        }
    }

    handleMouseUp(){
        if(this.state.activateListeners){
            if(this.state.position < -this.state.threshold){
                this.moveSlide(-1);
            } else if(this.state.position > this.state.threshold){
                this.moveSlide(1);
            } else {
                this.moveSlide(0);
            }

            this.setState({activateListeners: false});
        }
    }

    handleMouseOut(){
        // This stops the carousel bugging out if you mouse down and drag outside the carousel
        if(this.state.activateListeners){
            this.moveSlide(0);
            this.setState({activateListeners: false});
        }
    }

    setThreshold(){
        if(window.innerWidth < 568){
            this.setState({threshold: 50});
        } else {
            this.setState({threshold: 150});
        }
    }

    moveSlide(direction){
        // Check if autoplay is enabled and reset timer.
        if(this.state.autoplay){
            this.autoplayHandler();
        }

        if (direction === 'left') direction = -1;
        if (direction === 'right') direction = 1;

        this.setState({position: direction * this.state.slideWidth, activateTransition: true});

        setTimeout(() => {
            const firstSlide = this.carousel.current.firstElementChild;
            const lastSlide = this.carousel.current.lastElementChild;

            if(direction === 1){
                firstSlide.insertAdjacentElement('beforebegin', lastSlide);
            } else if(direction === -1){
                lastSlide.insertAdjacentElement('afterend', firstSlide);
            }

            this.setState({activateTransition: false, position: 0});
        }, 400);
    }

    renderSlide(media, index){
        let path = media.path ? normaliseImagePath(media.path) : normaliseImagePath(media);

        let setLeft = false;
        if(this.props.images.length > 2){
            setLeft = true;
        }

        return (
            <div key={index} className={"slide " + ("slide-" + (index))} style={{ width: `${this.state.slideWidth}px`, left: `-${setLeft ? this.state.slideWidth : 'auto'}px` }}>
                <img src={path} alt={media.alt} />
                {media.alt &&
                    <div className="slide-info">
                        {media.alt}
                    </div>
                }
            </div>
        );
    }

    render () {

        return (
            <div className="embedded-gallery--container">
                <div className="embedded-gallery-wrapper">
                    <div className="embedded-gallery-track" ref={this.carouselTrack}>
                        <div className={"embedded-gallery" + (this.state.activateTransition ? ' transition' : '')}
                             style={{transform: 'translateX(' + this.state.position + 'px)'}}
                             ref={this.carousel}
                             onMouseDown={this.handleMouseDown}
                             onMouseMove={this.handleMouseMove}
                             onMouseUp={this.handleMouseUp}
                             onMouseOut={this.handleMouseOut}
                             onTouchStart={this.handleMouseDown}
                             onTouchMove={this.handleMouseMove}
                             onTouchEnd={this.handleMouseUp}
                        >
                            {
                                this.props.images.map((image, index) => {
                                    return this.renderSlide(image, index);
                                })
                            }
                        </div>
                    </div>
                </div>
                {
                    this.state.showControls && this.props.images.length > 1 &&
                    <div className="controls">
                        <button className="control-left" onClick={() => this.moveSlide('right')}></button>
                        <button className="control-right" onClick={() => this.moveSlide('left')}></button>
                    </div>
                }

            </div>
        );

    }
}